// ███ THE UNREAD COUNT ███
//
// T3 (2026-10-27, brief-comms-chat). Every number the badge and the list row
// render is computed here, from two documents the client already holds:
//
//   * the conversation, carrying `messagesPostedTotal` — a running total of
//     message creates, maintained by `onCommsMessageWritten`. What it is NOT is
//     written beside `applyMessageWritten` and matters: it never decreases, it
//     counts deleted messages, and a retried trigger can count one twice.
//   * the viewer's own marker under `people/{uid}/conversationReads/{convId}`,
//     carrying `lastReadAt`, `readTotal` and `mute`.
//
// ⚠ WHY A SUBTRACTION AND NOT A QUERY. "Messages newer than my marker" is a
// query per conversation, which is a listener per list row — the cost the T0
// comment in `firestore.rules` ruled out. The subtraction needs no reads beyond
// the two listeners the chat already runs.
//
// ⚠ THE ERROR IS ALWAYS IN THE SAME DIRECTION, and that is the design rather
// than a limitation. Every inaccuracy above — a double-counted create, a
// deleted message, a marker that has not caught up — makes the count too HIGH.
// A count that is too high shows a badge for a message already read, and the
// next open clears it. A count that is too low never surfaces a message at all.
// Nothing in this file may introduce an error in the second direction.
//
// ⚠ A MISSING MARKER IS NOT ZERO UNREAD. A viewer who has never opened a
// conversation has no marker document, and the honest answer is that everything
// in it is unread. Reading an absent `readTotal` as 0 is what produces that, so
// the blank is handled deliberately rather than coerced — `Number(undefined)` is
// NaN, and `Math.max(0, NaN)` is NaN, which renders as "NaN" in a badge.

import { timestampMillis } from './commsFeedOrder';

// Above this the badge stops being a number and becomes a direction. 99 is the
// conventional cut; what matters here is that the cap is applied to the LABEL
// and never to the value, so a total still sums correctly across rows.
export const BADGE_MAX = 99;

function nonNegativeInt(value) {
  // Allow-list the type before any arithmetic: '' , null, [] and true all
  // coerce to a plausible number, and a string '5' off a hand-edited document
  // would compare as a number in some places and a string in others.
  if (typeof value !== 'number' || !Number.isFinite(value)) return 0;
  if (value <= 0) return 0;
  return Math.floor(value);
}

// The conversation's running total, or 0 for a conversation that predates the
// field. 0 means "no message has been counted", so every such conversation
// reads as fully read — which is correct: the trigger starts counting at the
// next message, and nothing can say how many came before it.
export function postedTotal(conversation) {
  return nonNegativeInt(conversation && conversation.messagesPostedTotal);
}

export function readTotal(marker) {
  return nonNegativeInt(marker && marker.readTotal);
}

// The per-viewer mute boolean. Strict `=== true`: an absent marker, a marker
// written before the field existed and a marker carrying `mute: null` are all
// unmuted, and only an explicit true silences a conversation.
export function isMuted(marker) {
  return Boolean(marker) && marker.mute === true;
}

// Unread messages in one conversation for one viewer.
//
// Clamped at zero because `readTotal` can legitimately exceed the total it was
// copied from: the viewer's marker is written from the total they could see, and
// a conversation document that has since been restored from a backup, or whose
// total was repaired downwards by hand, would otherwise produce a negative that
// subtracts from the nav badge and hides another conversation's unread.
export function unreadFor(conversation, marker) {
  return Math.max(0, postedTotal(conversation) - readTotal(marker));
}

// The nav badge's number: unread across every conversation the viewer can see,
// muted ones excluded.
//
// ⚠ MUTED IS EXCLUDED HERE AND IN THE ROW, from this one predicate. Writing
// `marker.mute !== true` at the two call sites would be two chances to disagree,
// and the disagreement a reader would notice is the badge counting a
// conversation whose row shows nothing.
export function totalUnread(conversations, markersById) {
  if (!Array.isArray(conversations)) return 0;
  const markers = markersById && typeof markersById === 'object' ? markersById : {};
  let total = 0;
  for (const conversation of conversations) {
    if (!conversation || typeof conversation.id !== 'string') continue;
    const marker = markers[conversation.id];
    if (isMuted(marker)) continue;
    total += unreadFor(conversation, marker);
  }
  return total;
}

// What the badge SAYS. '' when there is nothing to show, so a caller renders the
// badge on a non-empty label rather than on a separate `> 0` test that could
// disagree with it.
export function badgeLabel(count) {
  const n = nonNegativeInt(count);
  if (n === 0) return '';
  return n > BADGE_MAX ? `${BADGE_MAX}+` : String(n);
}

// The number of conversations with anything unread, muted ones excluded. Used
// for nothing on screen yet; it exists because `totalUnread` answers "how many
// messages" and a reader of the nav badge may well mean "how many threads", and
// the two numbers being different is worth being able to state.
export function unreadConversationCount(conversations, markersById) {
  if (!Array.isArray(conversations)) return 0;
  const markers = markersById && typeof markersById === 'object' ? markersById : {};
  let rows = 0;
  for (const conversation of conversations) {
    if (!conversation || typeof conversation.id !== 'string') continue;
    const marker = markers[conversation.id];
    if (isMuted(marker)) continue;
    if (unreadFor(conversation, marker) > 0) rows += 1;
  }
  return rows;
}

// The id of the message the thread's "new messages" divider sits above, or null.
//
// `messages` must already be in ascending order — `threadRows` sorts, and this
// is called with the same array it is given.
//
// ⚠ THE VIEWER'S OWN MESSAGES ARE SKIPPED. A message I sent is read by
// definition, and the running total counts it, so without this a viewer who
// posts and leaves sees a divider labelled "new" above their own words. The
// count has the same property and is corrected differently — by the marker being
// re-written whenever the open thread is at the bottom — because the count
// cannot name which message was whose.
//
// ⚠ AND A MARKER WITH NO `lastReadAt` RETURNS THE FIRST MESSAGE NOT MINE, not
// null. No marker means nothing has been read.
export function firstUnreadId(messages, marker, viewerUid) {
  if (!Array.isArray(messages) || messages.length === 0) return null;
  const readMs = timestampMillis(marker && marker.lastReadAt);
  const uid = typeof viewerUid === 'string' ? viewerUid : '';

  for (const message of messages) {
    if (!message || typeof message.id !== 'string') continue;
    if (uid !== '' && message.authorId === uid) continue;
    if (readMs === null) return message.id;
    const ms = timestampMillis(message.postedAt);
    // A message with no readable `postedAt` cannot be placed against the
    // marker. It is NOT treated as unread: the divider is a claim about a
    // boundary in time, and anchoring it on a message that has no time would
    // put it in an arbitrary place in the thread.
    if (ms === null) continue;
    if (ms > readMs) return message.id;
  }
  return null;
}
